import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Terminal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="about" className="py-20 relative">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent"
          >
            {t('about.title')}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left side - Image placeholder */}
            <motion.div variants={itemVariants} className="flex justify-center">
              <div className="w-full max-w-md aspect-square glass glow rounded-2xl flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-500/10 to-blue-600/10">
                <div className="w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/30 flex items-center justify-center backdrop-blur-sm">
                  <Terminal className="w-32 h-32 text-blue-500 opacity-40" strokeWidth={1} />
                </div>
              </div>
            </motion.div>

            {/* Right side - Text */}
            <motion.div variants={itemVariants} className="space-y-6">
              <p 
                className="text-lg text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('about.description1') }}
              />

              <p 
                className="text-lg text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('about.description2') }}
              />

              <p className="text-lg text-gray-300 leading-relaxed">
                {t('about.description3')}
              </p>

              <p 
                className="text-lg text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('about.description4') }}
              />

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="glass px-6 py-3 rounded-lg glow-hover">
                  <p className="text-2xl font-bold text-blue-500">5+</p>
                  <p className="text-sm text-gray-400">{t('about.yearsExperience')}</p>
                </div>
                <div className="glass px-6 py-3 rounded-lg glow-hover">
                  <p className="text-2xl font-bold text-blue-500">20+</p>
                  <p className="text-sm text-gray-400">{t('about.projectsCompleted')}</p>
                </div>
                <div className="glass px-6 py-3 rounded-lg glow-hover">
                  <p className="text-2xl font-bold text-blue-500">1000+</p>
                  <p className="text-sm text-gray-400">{t('about.activeUsers')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
